import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { subscribeAuth, login, logout, getAuth, getToken } from '../auth';
import { subscribeGameState } from '../gameState';
import { serverUrl, socket } from '../socket';
import { OWNER_ID } from '../mashupWhitelist';
import { GAME_MODES } from '../gameModes';
import { useI18n } from '../useI18n.js';
import LanguageSwitch from './LanguageSwitch';

// Panel owner : (de)active chaque mode par typologie (admins/joueurs). Temps reel.
function AdminModes() {
  const { t } = useI18n();
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    const onCfg = (d) => setCfg(d?.config || null);
    socket.on('modeAccessConfig', onCfg);
    socket.on('modeAccessChanged', onCfg);
    socket.emit('getModeAccess');
    return () => { socket.off('modeAccessConfig', onCfg); socket.off('modeAccessChanged', onCfg); };
  }, []);
  const set = (mode, typ, val) => socket.emit('setModeAccess', { mode, typology: typ, value: val });
  const c = cfg || {};
  return (
    <div style={{ marginTop: 4 }}>
      <h3 style={{ margin: '0 0 8px' }}>🛠️ {t('macTitle') || 'Accès aux modes'}</h3>
      <div className="mode-access-grid">
        <div className="mac-head" />
        <div className="mac-head">{t('macAdmins') || 'Admins'}</div>
        <div className="mac-head">{t('macPlayers') || 'Joueurs'}</div>
        {GAME_MODES.map((m) => (
          <React.Fragment key={m.id}>
            <div className="mac-mode">{m.emoji} {t(m.nameKey)}</div>
            <div className="mac-cell"><input type="checkbox" checked={c[m.id]?.admins ?? true} onChange={(e) => set(m.id, 'admins', e.target.checked)} /></div>
            <div className="mac-cell"><input type="checkbox" checked={c[m.id]?.players ?? true} onChange={(e) => set(m.id, 'players', e.target.checked)} /></div>
          </React.Fragment>
        ))}
      </div>
      <div style={{ fontSize: '.72rem', opacity: .7, marginTop: 6 }}>{t('macHint') || 'Toi (owner) as toujours accès à tous les modes. Temps réel.'}</div>
    </div>
  );
}

function avatarUrl(user) {
  if (!user) return 'https://cdn.discordapp.com/embed/avatars/0.png';
  if (user.avatar) return `https://cdn.discordapp.com/avatars/${user.discordId}/${user.avatar}.png?size=64`;
  return 'https://cdn.discordapp.com/embed/avatars/0.png';
}

// Panel owner : redefinir l'ELO de n'importe quel joueur.
function EloAdmin() {
  const { t } = useI18n();
  const [players, setPlayers] = useState([]);
  const [q, setQ] = useState('');
  const [saving, setSaving] = useState(null);
  const [edited, setEdited] = useState({});
  const load = () => fetch(`${serverUrl}/admin/players`, { headers: { Authorization: `Bearer ${getToken()}` } })
    .then((r) => r.json()).then((d) => setPlayers(d.players || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = (id) => {
    const v = edited[id];
    if (v === undefined || v === '') return;
    setSaving(id);
    fetch(`${serverUrl}/admin/players/${id}/elo`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ elo: Number(v) }),
    }).then((r) => r.json()).then(() => { setEdited((s) => { const n = { ...s }; delete n[id]; return n; }); load(); }).finally(() => setSaving(null));
  };
  const filtered = players.filter((p) => !q || (p.globalName || p.username || '').toLowerCase().includes(q.toLowerCase()));
  return (
    <div style={{ marginTop: 4 }}>
      <h3 style={{ margin: '0 0 8px' }}>🏆 {t('eloAdminTitle') || 'Gestion ELO'}</h3>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('eloAdminSearch') || 'Rechercher un joueur...'}
        style={{ width: '100%', marginBottom: 8, padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6 }} />
      <div style={{ maxHeight: 240, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
        {filtered.map((p) => (
          <div key={p.discordId} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <img src={avatarUrl(p)} alt="" style={{ width: 24, height: 24, borderRadius: '50%' }} />
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.globalName || p.username}</span>
            <input type="number" defaultValue={p.elo} min={0} onChange={(e) => setEdited((s) => ({ ...s, [p.discordId]: e.target.value }))}
              style={{ width: 82, padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: 6 }} />
            <button className="btn" disabled={saving === p.discordId || edited[p.discordId] === undefined} onClick={() => save(p.discordId)}>{t('eloAdminSet') || 'OK'}</button>
          </div>
        ))}
        {filtered.length === 0 && <div style={{ opacity: 0.6 }}>...</div>}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={{ flex: 1, background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
      <div style={{ fontSize: '1.2rem', fontWeight: 800 }}>{value ?? 0}</div>
      <div style={{ fontSize: '0.7rem', opacity: 0.7 }}>{label}</div>
    </div>
  );
}

function ProfileModal({ user, onClose }) {
  const { t } = useI18n();
  const [board, setBoard] = useState([]);
  const [tab, setTab] = useState('profil');
  useEffect(() => {
    let alive = true;
    fetch(`${serverUrl}/leaderboard`)
      .then((r) => r.json())
      .then((d) => { if (alive) setBoard(d.leaderboard || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const isOwner = user.discordId === OWNER_ID;
  const tabs = [
    { id: 'profil', label: t('tabProfile') || 'Profil', icon: '👤' },
    { id: 'params', label: t('settingsTitle') || 'Paramètres', icon: '⚙️' },
    ...(isOwner ? [
      { id: 'modes', label: t('macTitle') || 'Modes', icon: '🛠️' },
      { id: 'elo', label: t('eloAdminTitle') || 'ELO', icon: '🏆' },
    ] : []),
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal profile-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460, textAlign: 'left', color: '#2f3441', display: 'flex', flexDirection: 'column', maxHeight: '85vh' }}>
        {/* En-tete (toujours visible) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img src={avatarUrl(user)} alt="" style={{ width: 48, height: 48, borderRadius: '50%' }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: '1.05rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.globalName || user.username}</div>
            <div style={{ opacity: 0.7, fontSize: '0.8rem' }}>@{user.username}</div>
          </div>
        </div>

        {/* Onglets */}
        <div className="profile-tabs">
          {tabs.map((tb) => (
            <button key={tb.id} type="button" className={`profile-tab${tab === tb.id ? ' active' : ''}`} onClick={() => setTab(tb.id)}>
              <span aria-hidden>{tb.icon}</span> {tb.label}
            </button>
          ))}
        </div>

        {/* Contenu (scrollable) */}
        <div className="profile-tab-content" style={{ overflowY: 'auto', flex: 1, minHeight: 0, paddingRight: 4 }}>
          {tab === 'profil' && (
            <>
              <div style={{ display: 'flex', gap: 12 }}>
                <Stat label="ELO" value={user.elo} />
                <Stat label="Parties" value={user.games} />
                <Stat label="Victoires" value={user.wins} />
              </div>
              <h3 style={{ marginTop: 16, marginBottom: 6 }}>Classement</h3>
              <ol style={{ margin: 0, paddingLeft: 20 }}>
                {board.map((u) => (
                  <li key={u.discordId} style={{ fontWeight: u.discordId === user.discordId ? 800 : 400 }}>
                    {(u.globalName || u.username)} - {u.elo} ELO{' '}
                    <span style={{ opacity: 0.6, fontSize: '0.75rem' }}>({u.wins}/{u.games})</span>
                  </li>
                ))}
                {board.length === 0 && <li style={{ opacity: 0.6 }}>Aucun joueur classe.</li>}
              </ol>
            </>
          )}
          {tab === 'params' && (
            <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontSize: '.9rem', fontWeight: 600 }}>
              <span>{t('languageLabel') || 'Langue'}</span>
              <LanguageSwitch style={{ border: '2px solid #2f3441', background: '#fff', color: '#2f3441', cursor: 'pointer' }} />
            </label>
          )}
          {tab === 'modes' && isOwner && <AdminModes />}
          {tab === 'elo' && isOwner && <EloAdmin />}
        </div>

        {/* Pied (toujours visible) */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, borderTop: '2px solid #e5e7eb', paddingTop: 12 }}>
          <button className="btn" onClick={() => { logout(); onClose(); }} style={{ color: '#dc2626' }}>Se deconnecter</button>
          <button className="btn" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}

export function LoginBadge() {
  const [auth, setAuth] = useState(getAuth());
  const [open, setOpen] = useState(false);
  const [inGame, setInGame] = useState(false);
  useEffect(() => subscribeAuth(setAuth), []);
  useEffect(() => subscribeGameState(setInGame), []);
  const user = auth.user;

  // Masque le badge pendant une partie : evite le logout qui reconnecte le
  // socket et casse la room en pleine partie.
  if (inGame) return null;

  return (
    <div style={{ position: 'fixed', top: 8, right: 8, zIndex: 1000 }}>
      {!user ? (
        <button
          type="button"
          onClick={login}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#5865F2', color: '#fff', border: '2px solid #3b45c4', padding: '0.4rem 0.7rem', borderRadius: 4, cursor: 'pointer', fontWeight: 700, fontSize: '0.78rem', boxShadow: '0 3px 0 rgba(0,0,0,0.25)' }}
        >Se connecter</button>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#fff', color: '#2f3441', border: '2px solid #2f3441', padding: '0.25rem 0.5rem', borderRadius: 0, cursor: 'pointer', fontWeight: 700, fontSize: '0.78rem', boxShadow: '0 3px 0 rgba(0,0,0,0.2)' }}
        >
          <img src={avatarUrl(user)} alt="" style={{ width: 24, height: 24, borderRadius: '50%' }} />
          <span>{user.globalName || user.username}</span>
          <span style={{ background: '#5663f7', color: '#fff', borderRadius: 0, padding: '1px 8px', fontSize: '0.72rem', fontWeight: 800 }}>{user.elo} ELO</span>
        </button>
      )}
      {open && user && createPortal(<ProfileModal user={user} onClose={() => setOpen(false)} />, document.body)}
    </div>
  );
}
